import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {SHOW_MORE_BUTTON_VALUE, ShowMoreOption} from 'search/ShowMoreOption';
import {faded} from 'styles/Utilities';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {
  DefaultOptionContent,
  getMatchingOptionDatas,
  optionDatasWithSubmenuTransform,
  partitionOptionDatas,
} from 'ui-library/menus/MenuUtil';
import {Optional} from 'util/Types';

export interface FilterDropdownOptionsProps {
  optionDatas: Array<OptionData>;
  onChangeValue: (value: string) => void;
  renderOptionContent: (optionData: OptionData) => JSX.Element;
  getMatchingOptionDatas: (query: string, optionDatas: Array<OptionData>) => Array<OptionData>;
}

const DEFAULT_RENDER_OPTION_CONTENT = (optionData: OptionData, shouldFade: boolean): JSX.Element => (
  <DefaultOptionContent optionData={optionData} className={css(shouldFade && styles.fadedOption)} />
);

/**
 * React hook that can be used to turn a regular dropdown component into one that filters out certain options
 * by default, with a button to make them visible.
 *
 * Expected usage:
 * - Spread the object returned by this hook into the props being passed to a dropdown component
 * - You may need to map the properties to different prop names if the dropdown component in question expects
 * the same conceptual props, but with different names
 *
 * Can also be used by other hooks to implement specific reusable filtering behavior; see
 * useFilterArchivedDropdownOptions for an example.
 *
 * @param optionDatas - the options for the dropdown component that's using this hook
 * @param filterFunc - the function to be used to filter dropdown options
 * @param showMoreText - the text to display on the button that shows the hidden options
 * @param onChangeValueBase - the onChangeValue fn defined in the dropdown component that's using this hook
 * @param renderOptionContentBase - optional. If the component using this hook has a custom implementation of
 *  renderOptionContent, provide it here. Otherwise, DEFAULT_RENDER_OPTION_CONTENT will be used.
 */
export default function useFilterDropdownOptions(
  optionDatas: Array<OptionData>,
  filterFunc: (optionData: OptionData) => boolean,
  showMoreText: string,
  onChangeValueBase: (value: OptionValue) => void,
  renderOptionContentBase: (
    optionData: OptionData,
    shouldFade: boolean
  ) => JSX.Element = DEFAULT_RENDER_OPTION_CONTENT
): FilterDropdownOptionsProps {
  const [isShowingMore, setIsShowingMore] = useState(false);

  const onChangeValue = (value: OptionValue): void => {
    if (value === SHOW_MORE_BUTTON_VALUE) {
      setIsShowingMore(true);
    } else {
      onChangeValueBase(value);
    }
  };

  const getFilteredMatchingOptionDatas = (
    query: string,
    allOptionDatas: Array<OptionData>
  ): Array<OptionData> => {
    const matchingOptionDatas = getMatchingOptionDatas(query, allOptionDatas);
    const filteredToplevelOptions = matchingOptionDatas.filter((optionData) => filterFunc(optionData));
    const filteredOptionDatas = optionDatasWithSubmenuTransform(
      filteredToplevelOptions,
      (submenuSection: Array<Optional<OptionData>>) =>
        submenuSection.filter((submenuOption: Optional<OptionData>) =>
          submenuOption ? filterFunc(submenuOption) : submenuOption
        )
    );
    if (query !== '' && filteredOptionDatas.length < matchingOptionDatas.length) {
      // Only include the 'Show more' button if the user has entered a query and some options are being hidden by the filter
      return [...filteredOptionDatas, {name: showMoreText, value: SHOW_MORE_BUTTON_VALUE}];
    } else {
      return filteredOptionDatas;
    }
  };

  const renderOptionContentOrShowMoreOption = (optionData: OptionData): JSX.Element => {
    if (optionData.value === SHOW_MORE_BUTTON_VALUE) {
      return (
        <ShowMoreOption
          name={optionData.name}
          onClick={() => {
            onChangeValue(SHOW_MORE_BUTTON_VALUE);
          }}
        />
      );
    } else {
      return renderOptionContentBase(optionData, !filterFunc(optionData));
    }
  };

  return {
    // Partition the option datas so that those not matching the filter come last
    optionDatas: partitionOptionDatas(optionDatas, filterFunc),
    onChangeValue,
    renderOptionContent: (optionData: OptionData) => renderOptionContentOrShowMoreOption(optionData),
    // Explicitly provide two separate functions here, instead of one function that branches on isShowingMore.
    // This is necessary because we need to ensure that if isShowingMore changes, the props to components
    // using this hook also change so that they correctly re-render.
    getMatchingOptionDatas: isShowingMore ? getMatchingOptionDatas : getFilteredMatchingOptionDatas,
  };
}

const styles = StyleSheet.create({
  showMoreOption: {
    display: 'flex',
    flex: 1,
  },
  fadedOption: faded,
});
